import { useEffect, useState } from 'react'
import { useLangue, useTextes } from '../LangueContexte'
import { distanceMetres, libelleVille, useVille } from '../VilleContexte'
import { Bandeau, Champ, ChampSuggere, Liste, Zone } from './Champs'
import { CaptureInApp, ChoixGalerie } from './Capture'
import { SelecteurDePoint } from './CarteDifferee'
import {
  creerAnnonce,
  creerBien,
  mesBiens,
  definirCouverture,
  rattacherPhoto,
  soumettreAnnonce,
  type AnnoncePayload,
  type ResultatSoumission,
  type TypeBien,
} from '../apiCatalogue'
import { ErreurApi } from '../api'
import { suggererQuartiers } from '../apiRecherche'
import { EQUIPEMENTS } from '../equipements'
import { dateCourte, montant, nombre } from '../formats'
import { interpoler, messageErreur } from '../i18n'
import { Bloc, Bouton, Icone, Image, Puce } from '../ui'

/** Les cinq étapes du §6.1 de la spec, dans l'ordre qu'il fixe. */
type Etape = 'localisation' | 'caracteristiques' | 'photos' | 'conditions' | 'publication'

const ETAPES: readonly Etape[] = ['localisation', 'caracteristiques', 'photos', 'conditions', 'publication']

const TYPES_DE_BIEN: readonly TypeBien[] = ['Apartment', 'House', 'Villa', 'Studio', 'Riad']

/**
 * Assistant de publication — §6.1 de la spec, revu le 13/09/2026 (maquette `docs/ui/pwa/publier.html`).
 *
 * **L'ordre des étapes n'est pas arbitraire.** La localisation vient en premier parce qu'elle est la
 * seule donnée non réparable à distance (§1.7) ; les photos avant les conditions parce que la
 * capture in-app demande du temps et de la lumière, et qu'un propriétaire qui la découvre à la
 * dernière étape la sautera.
 *
 * **La largeur va à l'aparté, pas aux champs.** L'assistant n'est plus borné à 44 rem (règle du
 * 03/09) : au-delà de 60 rem, une colonne collante montre la carte d'annonce telle qu'elle
 * paraîtra dans les résultats et le récapitulatif qui se remplit d'étape en étape — ce que le
 * propriétaire vérifie, sans attendre la dernière étape. La colonne de saisie reste autour de
 * 50 rem, en deux ou trois colonnes pour les champs simples.
 */
export function AssistantPublication({ onTermine }: { onTermine: () => void }) {
  const t = useTextes()
  const langue = useLangue()
  const [etape, setEtape] = useState<Etape>('localisation')
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  // Localisation
  const [quartier, setQuartier] = useState('')
  const [quartiersConnus, setQuartiersConnus] = useState<string[]>([])

  // Suggestions rafraichies a la frappe, avec un court delai : sans lui, chaque touche partirait en
  // requete. 250 ms est le seuil au-dela duquel une proposition parait en retard.
  useEffect(() => {
    const minuteur = globalThis.setTimeout(() => {
      void suggererQuartiers(quartier)
        .then(setQuartiersConnus)
        .catch(() => setQuartiersConnus([]))
    }, 250)

    return () => globalThis.clearTimeout(minuteur)
  }, [quartier])
  /*
    La ville vient du RÉFÉRENTIEL (D44) : une liste, jamais une saisie — « Salé » et « Sale » ont
    divergé en une semaine sur le pilote. Toutes les villes actives, en amorçage comprises : un
    propriétaire peut publier dans une ville qui n'a pas encore d'annonce, c'est même ainsi qu'elle
    en aura. Pré-remplie avec la ville où l'on cherche.
  */
  const { villes, courante, trouver } = useVille()
  const [villeCode, setVilleCode] = useState('')

  useEffect(() => {
    if (villeCode === '' && courante !== null) {
      setVilleCode(courante.code)
    }
  }, [villeCode, courante])

  const villeChoisie = trouver(villeCode)
  const ville = villeChoisie === undefined ? '' : libelleVille(villeChoisie, langue)

  const [rue, setRue] = useState('')
  const [numero, setNumero] = useState('')
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)

  // Le repère sort de la ville déclarée : on le DIT, on ne bloque pas. La modération recevra une
  // détection `CityMismatch` (L13a) ; ici, c'est au propriétaire de vérifier lequel des deux se
  // trompe — et c'est le plus souvent la ville, pré-remplie.
  const horsRayon =
    villeChoisie !== undefined &&
    latitude !== null &&
    longitude !== null &&
    distanceMetres(latitude, longitude, villeChoisie.latitude, villeChoisie.longitude) > villeChoisie.radiusMetres

  // Caractéristiques
  const [type, setType] = useState<TypeBien>('Apartment')
  const [pieces, setPieces] = useState(3)
  const [chambres, setChambres] = useState(2)
  const [sallesDEau, setSallesDEau] = useState(1)
  const [etage, setEtage] = useState('')
  const [ascenseur, setAscenseur] = useState(false)
  const [surface, setSurface] = useState('')
  const [meuble, setMeuble] = useState(true)
  const [equipements, setEquipements] = useState<string[]>([])

  // Photos
  const [medias, setMedias] = useState<string[]>([])
  /** Celles prises dans l'application : c'est ce qui donne le badge « Photos originales ». */
  const [originales, setOriginales] = useState<string[]>([])
  const [captureOuverte, setCaptureOuverte] = useState(false)

  // Couverture choisie. `null` signifie « la première », qui est deja la regle du domaine :
  // AttachPhoto marque comme couverture la premiere photo rattachee. On ne stocke donc un
  // identifiant que lorsque l'utilisateur en designe une AUTRE, et l'appel reseau supplementaire
  // n'a lieu que dans ce cas.
  const [couverture, setCouverture] = useState<string | null>(null)

  // La couverture effective, celle qu'on affiche comme selectionnee.
  const couvertureEffective = couverture !== null && medias.includes(couverture) ? couverture : medias[0]

  function retirerPhoto(mediaId: string) {
    setMedias((actuels) => actuels.filter((m) => m !== mediaId))
    setOriginales((actuelles) => actuelles.filter((m) => m !== mediaId))

    // Retirer la photo de couverture ne doit pas laisser l'annonce sans couverture choisie : on
    // repasse au defaut, c'est-a-dire a la premiere restante.
    setCouverture((actuelle) => (actuelle === mediaId ? null : actuelle))
  }

  // Conditions
  const [titre, setTitre] = useState('')
  const [description, setDescription] = useState('')
  const [loyer, setLoyer] = useState('')
  const [charges, setCharges] = useState('')
  const [depot, setDepot] = useState('')
  const [dureeMin, setDureeMin] = useState(6)
  const [disponibleLe, setDisponibleLe] = useState(new Date().toISOString().slice(0, 10))
  const [bail, setBail] = useState<'SingleJoint' | 'Individual'>('SingleJoint')
  const [chambresOffertes, setChambresOffertes] = useState(0)
  const [chambresOccupees, setChambresOccupees] = useState(0)
  const [reglement, setReglement] = useState('')

  const [resultat, setResultat] = useState<ResultatSoumission | null>(null)

  const indexEtape = ETAPES.indexOf(etape)
  const loyerToutCompris = Number(loyer || 0) + Number(charges || 0)

  async function publier() {
    setOccupe(true)
    setErreur(null)

    try {
      const bien = await creerBien({
        type,
        address: {
          district: quartier,
          cityCode: villeCode,
          countryCode: 'MA',
          street: rue === '' ? null : rue,
          streetNumber: numero === '' ? null : numero,
          postalCode: null,
          latitude: latitude ?? 0,
          longitude: longitude ?? 0,
        },
        features: {
          floor: etage === '' ? null : Number(etage),
          hasElevator: ascenseur,
          totalArea: surface === '' ? null : Number(surface),
          roomCount: pieces,
          bedroomCount: chambres,
          bathroomCount: sallesDEau,
          isFurnished: meuble,
        },
        amenityCodes: equipements,
      })

      // Le bien naît avec son unité de logement entier : l'assistant n'a pas à parler d'« unités »,
      // un mot qui n'a de sens que pour nous. La coloc par chambre arrive à l'écran suivant.
      const biens = await mesBiens()
      const cree = biens.find((candidat) => candidat.id === bien.id)
      const unite = cree?.units[0]

      if (unite === undefined) {
        setErreur('unite_introuvable')
        return
      }

      const annonce: AnnoncePayload = {
        title: titre,
        description,
        language: 'fr',
        rentExcludingCharges: Number(loyer),
        chargesTotal: charges === '' ? 0 : Number(charges),
        securityDeposit: depot === '' ? 0 : Number(depot),
        currencyCode: 'MAD',
        minimumStayMonths: dureeMin,
        maximumStayMonths: null,
        availableFrom: disponibleLe,
        availableUntil: null,
        leaseType: bail,
        roomsOffered: chambresOffertes,
        roomsOccupied: chambresOccupees,
        houseRulesSummary: reglement === '' ? null : reglement,
      }

      const creee = await creerAnnonce(bien.id, unite.id, annonce)

      for (const mediaId of medias) {
        await rattacherPhoto(creee.id, mediaId, null)
      }

      // Uniquement si l'utilisateur a choisi autre chose que la premiere : le domaine a deja
      // marque celle-ci comme couverture en la rattachant.
      if (couvertureEffective !== undefined && couvertureEffective !== medias[0]) {
        await definirCouverture(creee.id, couvertureEffective)
      }

      setResultat(await soumettreAnnonce(creee.id))
    } catch (echec) {
      setErreur(echec instanceof ErreurApi ? echec.code : 'internal')
    } finally {
      setOccupe(false)
    }
  }

  if (resultat !== null) {
    return <Confirmation resultat={resultat} onTermine={onTermine} />
  }

  const libelleEtape: Record<Etape, string> = {
    localisation: t.publicationEmplacement,
    caracteristiques: t.publicationLeBien,
    photos: t.publicationPhotos,
    conditions: t.publicationEtapeConditions,
    publication: t.publicationVerification,
  }

  const libelleType: Record<TypeBien, string> = {
    Apartment: t.bienAppartement,
    House: t.bienMaison,
    Villa: t.bienVilla,
    Studio: t.bienStudio,
    Riad: t.bienRiad,
  }

  // Le récapitulatif de l'aparté : une ligne par étape franchie, « à renseigner » pour les autres.
  const bienResume = [
    libelleType[type],
    `${nombre(pieces, langue)} ${t.publicationPieces.toLowerCase()}`,
    surface === '' ? null : `${nombre(Number(surface), langue)} m²`,
  ]
    .filter((partie): partie is string => partie !== null)
    .join(' · ')

  const lignes: { etape: Etape; libelle: string; valeur: string }[] = [
    { etape: 'localisation', libelle: t.publicationEmplacement, valeur: [quartier, ville].filter((v) => v !== '').join(', ') },
    { etape: 'caracteristiques', libelle: t.publicationLeBien, valeur: bienResume },
    { etape: 'photos', libelle: t.publicationPhotos, valeur: nombre(medias.length, langue) },
    { etape: 'conditions', libelle: t.publicationLoyerToutCompris, valeur: loyer === '' ? '' : montant(loyerToutCompris, 'MAD', langue) },
    { etape: 'conditions', libelle: t.publicationDisponibleLe, valeur: dateCourte(disponibleLe, langue) },
  ]

  return (
    <section className="assistant">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.navPublier}</h2>
          <Puce>{interpoler(t.publicationEtape, { n: indexEtape + 1, total: ETAPES.length }, langue)}</Puce>
        </div>
        <p>{t.publicationIntro}</p>
      </div>

      {/*
        **Le fil de progression** — ticket `fo-publier-01`.

        Une pastille par étape, numérotée tant qu'elle est à venir, cochée une fois franchie, et un
        trait entre deux voisines. Le numéro répond à « où j'en suis », la coche à « qu'est-ce qui
        est acquis », et le trait fait le lien. Une étape franchie se rouvre d'un clic — c'est
        ainsi qu'on corrige une ligne du récapitulatif ; une étape à venir ne se saute pas.
      */}
      <ol className="etapes" aria-label={t.publierProgression}>
        {ETAPES.map((candidate, index) => (
          <li
            key={candidate}
            aria-current={index === indexEtape ? 'step' : undefined}
            className={index === indexEtape ? 'est-active' : index < indexEtape ? 'est-faite' : ''}
          >
            {/* La pastille est décorative : le libellé à côté nomme l'étape, et `aria-current`
                porte « celle-ci ». */}
            <span className="etapes__puce" aria-hidden="true">
              {index < indexEtape ? <Icone nom="coche" taille="sm" /> : index + 1}
            </span>
            {index < indexEtape ? (
              <button type="button" className="lien" onClick={() => setEtape(candidate)}>
                {libelleEtape[candidate]}
              </button>
            ) : (
              libelleEtape[candidate]
            )}
          </li>
        ))}
      </ol>

      <div className="avec-aparte">
        <div className="pile">
          {etape === 'localisation' && (
            <Bloc titre={t.publicationEmplacement} actions={<span className="tertiaire">{t.publicationAdresseJamais}</span>}>
              <div className="formulaire formulaire--deux">
                <ChampSuggere
                  id="c-quartier"
                  libelle={t.publicationQuartier}
                  valeur={quartier}
                  onChange={setQuartier}
                  suggestions={quartiersConnus}
                  aide={t.publicationQuartierAide}
                  requis
                />
                <Liste
                  id="c-ville"
                  libelle={t.publicationVille}
                  valeur={villeCode}
                  options={villes.map((candidate) => ({
                    valeur: candidate.code,
                    libelle:
                      candidate.status === 'Open'
                        ? libelleVille(candidate, langue)
                        : `${libelleVille(candidate, langue)} · ${t.villePasEncoreOuverte}`,
                  }))}
                  aide={t.publicationVilleAide}
                  onChange={setVilleCode}
                />
                <Champ id="c-rue" libelle={t.publicationRue} valeur={rue} onChange={setRue} />
                <Champ id="c-numero" libelle={t.publicationNumero} valeur={numero} onChange={setNumero} />

                <div className="champ pleine-largeur">
                  <span className="champ__libelle">{t.publicationPositionLogement}</span>
                  <SelecteurDePoint
                    latitude={latitude}
                    longitude={longitude}
                    centre={villeChoisie}
                    onDeplacer={(lat, lng) => {
                      setLatitude(lat)
                      setLongitude(lng)
                    }}
                  />
                  {latitude === null && <span className="aide">{t.publicationRepere}</span>}
                  {horsRayon && (
                    <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
                      {interpoler(t.publicationVilleHorsRayon, { ville })}
                    </Bandeau>
                  )}
                </div>
              </div>
            </Bloc>
          )}

          {etape === 'caracteristiques' && (
            <Bloc titre={t.publicationLeBien}>
              <div className="pile">
                <div className="champ">
                  <span className="champ__libelle" id="c-type-libelle">
                    {t.publicationTypeBien}
                  </span>
                  <div className="choix-puces" role="radiogroup" aria-labelledby="c-type-libelle">
                    {TYPES_DE_BIEN.map((candidat) => (
                      <button
                        key={candidat}
                        type="button"
                        role="radio"
                        className="puce puce--action"
                        aria-checked={type === candidat}
                        aria-pressed={type === candidat}
                        onClick={() => setType(candidat)}
                      >
                        {libelleType[candidat]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="formulaire formulaire--trois">
                  <Champ id="c-pieces" libelle={t.publicationPieces} valeur={String(pieces)} onChange={(v) => setPieces(Number(v) || 1)} type="number" />
                  <Champ id="c-chambres" libelle={t.publicationChambres} valeur={String(chambres)} onChange={(v) => setChambres(Number(v) || 0)} type="number" />
                  <Champ id="c-salles-eau" libelle={t.publicationSallesDEau} valeur={String(sallesDEau)} onChange={(v) => setSallesDEau(Number(v) || 0)} type="number" />
                  <Champ id="c-etage" libelle={t.publicationEtage} valeur={etage} onChange={setEtage} type="number" />
                  <Champ id="c-surface" libelle={t.publicationSurface} valeur={surface} onChange={setSurface} type="number" />
                </div>

                <div className="rangee" style={{ gap: 'var(--e-6)' }}>
                  <label className="interrupteur">
                    <input type="checkbox" checked={ascenseur} onChange={(e) => setAscenseur(e.target.checked)} />
                    <span className="interrupteur__piste" />
                    {t.publicationAscenseur}
                  </label>
                  <label className="interrupteur">
                    <input type="checkbox" checked={meuble} onChange={(e) => setMeuble(e.target.checked)} />
                    <span className="interrupteur__piste" />
                    {t.publicationMeuble}
                  </label>
                </div>

                <div className="champ">
                  <span className="champ__libelle" id="c-equipements-libelle">
                    {t.publicationEquipements}
                  </span>
                  <div className="choix-puces" role="group" aria-labelledby="c-equipements-libelle">
                    {EQUIPEMENTS.map((equipement) => {
                      const present = equipements.includes(equipement.code)

                      return (
                        <button
                          key={equipement.code}
                          type="button"
                          className="puce puce--action"
                          aria-pressed={present}
                          onClick={() =>
                            setEquipements((actuels) =>
                              present ? actuels.filter((code) => code !== equipement.code) : [...actuels, equipement.code],
                            )
                          }
                        >
                          <Icone nom={equipement.icone} taille="sm" />
                          {t[equipement.libelle]}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </Bloc>
          )}

          {etape === 'photos' && (
            <Bloc titre={t.publicationPhotos} actions={medias.length > 0 ? <Puce>{nombre(medias.length, langue)}</Puce> : undefined}>
              <div className="pile">
                <Bandeau ton="info" icone={<Icone nom="appareil" />}>
                  {t.publicationCaptureAide}
                </Bandeau>

                {captureOuverte ? (
                  <CaptureInApp
                    listingId={null}
                    onCapture={(mediaId) => {
                      // La session reste ouverte : on photographie une chambre en plusieurs fois.
                      setMedias((actuels) => [...actuels, mediaId])
                      setOriginales((actuelles) => [...actuelles, mediaId])
                    }}
                    onFermer={() => setCaptureOuverte(false)}
                  />
                ) : (
                  <div className="assistant__actions">
                    <Bouton onClick={() => setCaptureOuverte(true)}>
                      <Icone nom="appareil" taille="sm" /> {t.publicationPhotographier}
                    </Bouton>
                    <ChoixGalerie listingId={null} onCapture={(mediaId) => setMedias((a) => [...a, mediaId])} />
                  </div>
                )}

                {medias.length > 0 && (
                  <ul className="grille-photos" aria-label={t.publicationPhotos}>
                    {medias.map((mediaId, rang) => {
                      const estCouverture = couvertureEffective === mediaId

                      return (
                        <li key={mediaId} className={estCouverture ? 'vignette-photo est-couverture' : 'vignette-photo'}>
                          {originales.includes(mediaId) && (
                            <span className="puce vignette-photo__tag">
                              <Icone nom="appareil" taille="sm" /> {t.publicationPhotoOriginale}
                            </span>
                          )}
                          <button
                            type="button"
                            className="vignette-photo__retirer"
                            aria-label={interpoler(t.publicationRetirerPhoto, { n: rang + 1 }, langue)}
                            onClick={() => retirerPhoto(mediaId)}
                          >
                            <Icone nom="croix" taille="sm" />
                          </button>
                          <Image mediaId={mediaId} alt="" ratio="1-1" sizes="10rem" />
                          {/* La couverture se voit sur la vignette ; le bouton dit ce que le clic fait. */}
                          {estCouverture ? (
                            <Puce ton="accent">{t.publicationCouverture}</Puce>
                          ) : (
                            <Bouton apparence="discret" taille="sm" onClick={() => setCouverture(mediaId)}>
                              {t.publicationCouverture}
                            </Bouton>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}

                <p className="aide">
                  {medias.length > 1 ? `${t.publicationCouvertureAide} ` : ''}
                  {t.publicationPhotosExif}
                </p>
                {medias.length === 0 && <p className="aide">{t.publicationPhotoRequise}</p>}
              </div>
            </Bloc>
          )}

          {etape === 'conditions' && (
            <Bloc titre={t.publicationEtapeConditions}>
              <div className="fiche__section">
                <h3>{t.publicationAnnonce}</h3>
                <div className="formulaire">
                  <Champ id="c-titre" libelle={t.publicationTitre} valeur={titre} onChange={setTitre} requis />
                  <Zone id="c-description" libelle={t.publicationDescription} lignes={5} valeur={description} onChange={setDescription} />
                </div>
              </div>

              <div className="fiche__section">
                <h3>{t.publicationLoyerEtCharges}</h3>
                <div className="formulaire formulaire--trois">
                  <Champ id="c-loyer" libelle={t.publicationLoyerHorsCharges} suffixe="MAD" valeur={loyer} onChange={setLoyer} type="number" requis />
                  <Champ id="c-charges" libelle={t.publicationChargesMensuelles} suffixe="MAD" valeur={charges} onChange={setCharges} type="number" />
                  <Champ id="c-depot" libelle={t.publicationDepot} suffixe="MAD" aide={t.publicationDeuxMois} valeur={depot} onChange={setDepot} type="number" />
                </div>
                {loyer !== '' && (
                  <Bandeau ton="info" icone={<Icone nom="monnaie" />}>
                    {interpoler(t.publicationLoyerAffiche, { montant: montant(loyerToutCompris, 'MAD', langue) })}
                  </Bandeau>
                )}
              </div>

              <div className="fiche__section">
                <h3>{t.publicationBailDisponibilite}</h3>
                <div className="champ">
                  <span className="champ__libelle" id="c-bail-libelle">
                    {t.publicationTypeBail}
                  </span>
                  <div className="choix-puces" role="radiogroup" aria-labelledby="c-bail-libelle">
                    {(['SingleJoint', 'Individual'] as const).map((candidat) => (
                      <button
                        key={candidat}
                        type="button"
                        role="radio"
                        className="puce puce--action"
                        aria-checked={bail === candidat}
                        aria-pressed={bail === candidat}
                        onClick={() => setBail(candidat)}
                      >
                        {candidat === 'SingleJoint' ? t.bailSolidaire : t.bailIndividuel}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="formulaire formulaire--deux">
                  <Champ id="c-disponible" libelle={t.publicationDisponibleLe} valeur={disponibleLe} onChange={setDisponibleLe} type="date" />
                  <Champ id="c-duree-min" libelle={t.publicationDureeMinimale} valeur={String(dureeMin)} onChange={(v) => setDureeMin(Number(v) || 1)} type="number" />
                  <Champ id="c-chambres-offertes" libelle={t.publicationChambresProposees} valeur={String(chambresOffertes)} onChange={(v) => setChambresOffertes(Number(v) || 0)} type="number" />
                  <Champ id="c-chambres-occupees" libelle={t.publicationChambresOccupees} valeur={String(chambresOccupees)} onChange={(v) => setChambresOccupees(Number(v) || 0)} type="number" />
                </div>
              </div>

              {/* Pas de sous-titre : le libellé du champ suffit, un titre le répéterait. */}
              <div className="fiche__section">
                <div className="formulaire">
                  <Zone id="c-regles" libelle={t.publicationRegles} lignes={3} aide={t.publicationReglesAide} valeur={reglement} onChange={setReglement} />
                </div>
              </div>
            </Bloc>
          )}

          {etape === 'publication' && (
            <Bloc titre={t.publicationVerifiez} actions={<span className="tertiaire">{t.publicationCorrigerEtape}</span>}>
              <dl className="fiche__lignes">
                <div className="fiche__ligne">
                  <dt>{t.publicationEmplacement}</dt>
                  <dd>
                    {quartier}, {ville}
                  </dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.publicationLeBien}</dt>
                  <dd>
                    {[
                      bienResume,
                      etage === '' ? null : `${t.publicationEtage} ${nombre(Number(etage), langue)}`,
                      ascenseur ? t.publicationAscenseur : null,
                      meuble ? t.publicationMeuble : null,
                    ]
                      .filter((partie): partie is string => partie !== null)
                      .join(' · ')}
                  </dd>
                </div>
                {equipements.length > 0 && (
                  <div className="fiche__ligne">
                    <dt>{t.publicationEquipements}</dt>
                    <dd>
                      {EQUIPEMENTS.filter((e) => equipements.includes(e.code))
                        .map((e) => t[e.libelle])
                        .join(', ')}
                    </dd>
                  </div>
                )}
                <div className="fiche__ligne">
                  <dt>{t.publicationPhotos}</dt>
                  <dd>
                    {originales.length > 0
                      ? interpoler(t.publicationPhotosDontOriginales, { n: medias.length, originales: originales.length }, langue)
                      : nombre(medias.length, langue)}
                  </dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.publicationLoyerToutCompris}</dt>
                  <dd>
                    <bdi>{montant(loyerToutCompris, 'MAD', langue)}</bdi>
                  </dd>
                </div>
                {depot !== '' && (
                  <div className="fiche__ligne">
                    <dt>{t.publicationDepot}</dt>
                    <dd>
                      <bdi>{montant(Number(depot), 'MAD', langue)}</bdi>
                    </dd>
                  </div>
                )}
                <div className="fiche__ligne">
                  <dt>{t.publicationTypeBail}</dt>
                  <dd>{bail === 'SingleJoint' ? t.bailSolidaire : t.bailIndividuel}</dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.publicationDisponibleLe}</dt>
                  <dd>
                    {dateCourte(disponibleLe, langue)} · {nombre(dureeMin, langue)} {t.uniteMois}
                  </dd>
                </div>
              </dl>

              <div className="pile" style={{ marginBlockStart: 'var(--e-4)' }}>
                <Bandeau ton="attention" icone={<Icone nom="carte" />}>
                  {t.publicationBrouillageAide}
                </Bandeau>
                <Bandeau ton="info" icone={<Icone nom="oeil" />}>
                  {t.publicationRelecture}
                </Bandeau>
                {erreur !== null && <Bandeau ton="erreur">{messageErreur(langue, erreur)}</Bandeau>}
              </div>

              <div className="rangee rangee--fin" style={{ marginBlockStart: 'var(--e-5)' }}>
                <Bouton taille="lg" charge={occupe} onClick={() => void publier()}>
                  {t.publicationPublier}
                </Bouton>
              </div>
            </Bloc>
          )}

          <div className="rangee rangee--espace">
            <Bouton apparence="secondaire" desactive={indexEtape === 0 || occupe} onClick={() => setEtape(ETAPES[indexEtape - 1])}>
              {t.precedent}
            </Bouton>
            {etape !== 'publication' && (
              <Bouton desactive={!etapeValide(etape)} onClick={() => setEtape(ETAPES[indexEtape + 1])}>
                {t.continuer}
              </Bouton>
            )}
          </div>
        </div>

        {/*
          L'annonce telle qu'elle paraîtra dans les résultats, et ce qui est déjà renseigné. Rien
          n'y est saisi : c'est un miroir, qui se remplit au fil des étapes.
        */}
        <aside className="aparte" aria-label={t.publicationApercu}>
          <Bloc titre={t.publicationApercu} actions={<span className="tertiaire">{t.publicationApercuSousTitre}</span>}>
            <div className="apercu" aria-hidden="true">
              <div className="apercu__media">
                <Image mediaId={couvertureEffective ?? null} alt="" ratio="1-1" sizes="20rem" vide={<Icone nom="image" taille="lg" />} />
                {originales.length > 0 && (
                  <span className="puce apercu__tag">
                    <Icone nom="appareil" taille="sm" /> {t.badgePhotos}
                  </span>
                )}
              </div>
              <div className="apercu__corps">
                <p className={titre === '' ? 'apercu__titre est-vide' : 'apercu__titre'}>{titre === '' ? t.publicationTitreVide : titre}</p>
                <p className="apercu__meta">
                  {[quartier || null, ville || null].filter((v) => v !== null).join(', ')}
                  {' · '}
                  {libelleType[type]}
                  {meuble ? ` · ${t.publicationMeuble.toLowerCase()}` : ''}
                </p>
                {loyer === '' ? (
                  <p className="apercu__prix est-vide">{t.publicationLoyerVide}</p>
                ) : (
                  <p className="apercu__prix">
                    <bdi>{montant(loyerToutCompris, 'MAD', langue)}</bdi> <span>{t.annonceParMois}</span>
                  </p>
                )}
              </div>
            </div>
            <dl className="fiche__lignes assistant__recapitulatif" style={{ marginBlockStart: 'var(--e-4)' }}>
              {lignes.map((ligne, rang) => {
                const rangEtape = ETAPES.indexOf(ligne.etape)
                const renseignee = rangEtape < indexEtape && ligne.valeur !== ''

                return (
                  <div className="fiche__ligne" key={rang}>
                    <dt>{ligne.libelle}</dt>
                    <dd className={renseignee ? undefined : 'est-vide'}>
                      {renseignee ? ligne.valeur : rangEtape === indexEtape ? t.publicationEtapeEnCours : t.publicationARenseigner}
                    </dd>
                  </div>
                )
              })}
            </dl>
          </Bloc>
          <Bandeau ton="info" icone={<Icone nom="bouclier" />}>
            {t.publicationAdresseAide}
          </Bandeau>
        </aside>
      </div>
    </section>
  )

  function etapeValide(courante: Etape): boolean {
    switch (courante) {
      case 'localisation':
        return quartier.trim() !== '' && villeCode !== '' && latitude !== null
      case 'caracteristiques':
        return pieces > 0 && chambres <= pieces
      case 'photos':
        return medias.length > 0
      case 'conditions':
        return titre.trim() !== '' && description.trim() !== '' && Number(loyer) > 0
      default:
        return true
    }
  }
}

/**
 * Ce que l'on dit au propriétaire après la soumission.
 *
 * **Le message dépend du régime de modération, que le front ne connaît pas.** Il lit `isVisible`,
 * que le serveur calcule : en publication ouverte l'annonce est déjà en ligne, en modération
 * bloquante elle attend. Dupliquer la règle ici garantirait qu'un jour les deux divergent — et le
 * propriétaire lirait « en ligne » sur une annonce que personne ne voit.
 */
function Confirmation({ resultat, onTermine }: { resultat: ResultatSoumission; onTermine: () => void }) {
  const t = useTextes()

  return (
    <section className="assistant">
      <Bloc variante="accent">
        <div className="pile">
          <h2 style={{ margin: 0 }}>{resultat.isVisible ? t.publicationEnLigne : t.publicationEnAttente}</h2>
          <p className="secondaire">{t.publicationRelecture}</p>
          <div>
            <Bouton onClick={onTermine}>{t.publicationVoirMesAnnonces}</Bouton>
          </div>
        </div>
      </Bloc>
    </section>
  )
}
